import { useState, useMemo, useCallback, memo, useRef } from "react";
import {
	Collapse,
	Table,
	Button,
	ButtonGroup,
	Form,
	Modal,
} from "react-bootstrap";
import useLocalStorageState from "use-local-storage-state";
import { helpers } from "../../util/helpers.ts";
import { toWorker } from "../../util/toWorker.ts";
import type { MinimalPlayerRatings } from "../../../common/types.ts";
import clsx from "clsx";
import { HelpPopover } from "../../components/HelpPopover.tsx";
import { RATINGS } from "../../../common/constants.ts";
import { getCol } from "../../../common/getCol.ts";
import { bySport } from "../../../common/sportFunctions.ts";
import RatingsForm from "./RatingsForm.tsx";

// Rating groups for tooltip display
const ratingGroups = bySport<Record<string, string[]>>({
	baseball: {
		"Physical/Hitting": ["hgt", "spd", "hpw", "con", "eye"],
		Defense: ["gnd", "fly", "thr", "cat"],
		Pitching: ["ppw", "ctl", "mov", "endu"],
	},
	basketball: {
		Physical: ["hgt", "stre", "spd", "jmp", "endu"],
		Shooting: ["ins", "dnk", "ft", "fg", "tp"],
		Skill: ["oiq", "diq", "drb", "pss", "reb"],
	},
	football: {
		Physical: ["hgt", "stre", "spd", "endu"],
		Passing: ["thv", "thp", "tha"],
		"Rush/Rec": ["elu", "rtr", "hnd", "bsc"],
		Blocking: ["rbk", "pbk"],
		Defense: ["pcv", "tck", "prs", "rns"],
		Kicking: ["kpw", "kac", "ppw", "pac"],
	},
	hockey: {
		Physical: ["hgt", "stre", "spd", "endu"],
		Offense: ["oiq", "pss", "wst", "sst", "stk"],
		Defense: ["diq", "chk", "blk", "fcf", "glk"],
	},
});

// Helper function to format ratings as a tooltip string (much faster than React components)
const formatRatingsTooltip = (ratings: MinimalPlayerRatings): string => {
	const lines: string[] = [];
	lines.push(`Overall: ${ratings.ovr}`);
	lines.push(`Potential: ${ratings.pot}`);
	if (ratings.pos) lines.push(`Position: ${ratings.pos}`);
	lines.push("");

	for (const [groupName, groupRatings] of Object.entries(ratingGroups)) {
		lines.push(groupName);
		for (const rating of groupRatings) {
			const value = (ratings as any)[rating];
			if (value !== undefined) {
				lines.push(`  ${getCol(`rating:${rating}`).desc}: ${value}`);
			}
		}
		lines.push("");
	}

	return lines.join("\n");
};

type SortBy =
	| "peak"
	| "most-ovrs"
	| "consistency"
	| "youngest-peak"
	| "oldest-peak"
	| "bust"
	| "prodigy"
	| "late-bloomer"
	| { type: "age"; age: number; direction: "asc" | "desc" };

const PlayerFutures = (props: {
	pid: number;
	currentForcedFuture?: Record<number, MinimalPlayerRatings>;
	season: number;
	currentAge: number;
	godMode: boolean;
	lockedRatings?: string[];
	onFutureSelected: (
		future: Record<number, MinimalPlayerRatings> | undefined,
	) => void;
	pos: string;
	superchargeRatings: boolean;
	challengeNoRatings: boolean;
	onToggleLockedRating: (rating: string) => void;
}) => {
	const {
		pid,
		currentForcedFuture,
		season,
		currentAge,
		godMode,
		lockedRatings,
		onFutureSelected,
		pos,
		superchargeRatings,
		challengeNoRatings,
		onToggleLockedRating,
	} = props;
	const [open, setOpen] = useState(true);
	const [editingAge, setEditingAge] = useState<number | null>(null);
	const [futures, setFutures] = useState<
		Record<number, MinimalPlayerRatings>[]
	>([]);
	const [loading, setLoading] = useState(false);
	const [sortBy, setSortBy] = useState<SortBy>("peak");
	const [numSimulations, setNumSimulations] = useState(500);
	const [minPeak, setMinPeak] = useState("");
	const [maxPeak, setMaxPeak] = useState("");
	const [luck, setLuck] = useState(0);
	const [realPlayerDeterminism, setRealPlayerDeterminism] = useState<
		number | undefined
	>(undefined);
	const [showYear, setShowYear] = useLocalStorageState(
		"player-futures-show-year",
		{
			defaultValue: false,
		},
	);
	const [lockedValues, setLockedValues] = useState<
		Record<number, MinimalPlayerRatings>
	>({});
	const [biasedRatings, setBiasedRatings] = useState<Record<string, number>>(
		{},
	);

	const [localRatings, setLocalRatings] = useState<MinimalPlayerRatings | null>(
		null,
	);
	const [prevEditingAge, setPrevEditingAge] = useState<number | null>(null);
	const lastOvrRequestRef = useRef<number>(0);

	const runSimulations = async () => {
		setLoading(true);
		try {
			const results = await toWorker("main", "simulatePlayerFutures", {
				pid,
				numSimulations,
				luck,
				lockedValues,
				realPlayerDeterminism,
				biasedRatings,
				lockedRatings,
			});
			setFutures(results);
		} catch (error) {
			console.error(error);
		} finally {
			setLoading(false);
		}
	};

	const isFutureSelected = useCallback(
		(future: Record<number, MinimalPlayerRatings>) => {
			if (!currentForcedFuture) {
				return false;
			}
			return JSON.stringify(future) === JSON.stringify(currentForcedFuture);
		},
		[currentForcedFuture],
	);

	const getPeakInfo = (future: Record<number, MinimalPlayerRatings>) => {
		let peakOvr = -1;
		let peakAge = -1;
		// Store all ages that have the current peakOvr
		let peakAges: number[] = [];

		for (const ageStr of Object.keys(future)) {
			const age = Number.parseInt(ageStr, 10);
			const r = future[age];
			if (!r) {
				continue;
			}
			if (r.ovr > peakOvr) {
				peakOvr = r.ovr;
				peakAge = age;
				peakAges = [age]; // Reset for a new, higher peak
			} else if (r.ovr === peakOvr) {
				peakAges.push(age); // Add age for same peak OVR
			}
		}
		// If there are multiple ages with the same peak OVR, pick the earliest
		if (peakAges.length > 1) {
			peakAge = Math.min(...peakAges);
		}
		return { peakOvr, peakAge };
	};

	const sortedFutures = useMemo(() => {
		let allFutures = [...futures];
		if (
			currentForcedFuture &&
			!allFutures.some((future) => isFutureSelected(future))
		) {
			allFutures = [currentForcedFuture, ...allFutures];
		}

		const filtered = allFutures.filter((future) => {
			const peakOvr = Math.max(...Object.values(future).map((r) => r.ovr));
			const min = minPeak !== "" ? Number.parseInt(minPeak, 10) : -Infinity;
			const max = maxPeak !== "" ? Number.parseInt(maxPeak, 10) : Infinity;
			return peakOvr >= min && peakOvr <= max;
		});

		const newFutures = [...filtered];

		if (typeof sortBy === "object" && sortBy.type === "age") {
			newFutures.sort((a, b) => {
				const rA = a[sortBy.age];
				const rB = b[sortBy.age];
				const valA = rA ? rA.ovr : -1;
				const valB = rB ? rB.ovr : -1;
				return sortBy.direction === "desc" ? valB - valA : valA - valB;
			});
		} else if (sortBy === "peak") {
			newFutures.sort((a, b) => {
				const peakA = Math.max(...Object.values(a).map((r) => r.ovr));
				const peakB = Math.max(...Object.values(b).map((r) => r.ovr));
				return peakB - peakA;
			});
		} else if (sortBy === "most-ovrs") {
			newFutures.sort((a, b) => {
				const sumA = Object.values(a).reduce((sum, r) => sum + r.ovr, 0);
				const sumB = Object.values(b).reduce((sum, r) => sum + r.ovr, 0);
				return sumB - sumA;
			});
		} else if (sortBy === "consistency") {
			const getReliabilityScore = (
				future: Record<number, MinimalPlayerRatings>,
			) => {
				const ovrs = Object.values(future).map((r) => r.ovr);
				const mean = ovrs.reduce((sum, ovr) => sum + ovr, 0) / ovrs.length;
				const variance =
					ovrs.reduce((sum, ovr) => sum + (ovr - mean) ** 2, 0) / ovrs.length;
				const stdDev = Math.sqrt(variance);
				return mean - 1.5 * stdDev;
			};

			newFutures.sort((a, b) => {
				return getReliabilityScore(b) - getReliabilityScore(a);
			});
		} else if (sortBy === "prodigy") {
			const PRODIGY_AGE_PENALTY_MULTIPLIER = 2.0;
			newFutures.sort((a, b) => {
				const { peakOvr: peakOvrA, peakAge: peakAgeA } = getPeakInfo(a);
				const { peakOvr: peakOvrB, peakAge: peakAgeB } = getPeakInfo(b);

				const scoreA = peakOvrA - PRODIGY_AGE_PENALTY_MULTIPLIER * peakAgeA;
				const scoreB = peakOvrB - PRODIGY_AGE_PENALTY_MULTIPLIER * peakAgeB;
				return scoreB - scoreA;
			});
		} else if (sortBy === "late-bloomer") {
			const LATE_BLOOMER_AGE_BONUS_MULTIPLIER = 1.5;
			newFutures.sort((a, b) => {
				const { peakOvr: peakOvrA, peakAge: peakAgeA } = getPeakInfo(a);
				const { peakOvr: peakOvrB, peakAge: peakAgeB } = getPeakInfo(b);

				const scoreA = peakOvrA + LATE_BLOOMER_AGE_BONUS_MULTIPLIER * peakAgeA;
				const scoreB = peakOvrB + LATE_BLOOMER_AGE_BONUS_MULTIPLIER * peakAgeB;
				return scoreB - scoreA;
			});
		} else if (sortBy === "bust") {
			newFutures.sort((a, b) => {
				const { peakOvr: peakOvrA, peakAge: peakAgeA } = getPeakInfo(a);
				const { peakOvr: peakOvrB, peakAge: peakAgeB } = getPeakInfo(b);

				// Primary sort: Earliest Peak Year (Ascending)
				if (peakAgeA !== peakAgeB) {
					return peakAgeA - peakAgeB;
				}
				// Secondary sort: Peak Rating (Ascending)
				return peakOvrA - peakOvrB;
			});
		}

		// Always move the selected future to the top
		if (currentForcedFuture) {
			const selectedIndex = newFutures.findIndex((f) => isFutureSelected(f));
			if (selectedIndex > -1) {
				const [selected] = newFutures.splice(selectedIndex, 1);
				newFutures.unshift(selected!);
			}
		}

		return newFutures;
	}, [
		futures,
		sortBy,
		minPeak,
		maxPeak,
		currentForcedFuture,
		isFutureSelected,
	]);

	if (editingAge !== prevEditingAge) {
		setPrevEditingAge(editingAge);
		if (editingAge !== null) {
			const source =
				currentForcedFuture ||
				(sortedFutures.length > 0 ? sortedFutures[0] : null);
			setLocalRatings(source?.[editingAge] ? { ...source[editingAge] } : null);
		} else {
			setLocalRatings(null);
		}
	}

	const toggleLock = (age: number) => {
		setLockedValues((prev) => {
			const next = { ...prev };
			if (next[age]) {
				delete next[age];
			} else {
				const source =
					currentForcedFuture ||
					(sortedFutures.length > 0 ? sortedFutures[0] : null);
				if (source) {
					const val = source[age];
					if (val) {
						next[age] = val;
					}
				}
			}
			return next;
		});
	};

	const areAllLocked = useMemo(() => {
		if (sortedFutures.length === 0 && !currentForcedFuture) {
			return false;
		}
		const source = currentForcedFuture || sortedFutures[0];
		if (!source) {
			return false;
		}
		const ages = Object.keys(source).map(Number);
		return ages.every((age) => !!lockedValues[age]);
	}, [sortedFutures, currentForcedFuture, lockedValues]);

	const toggleLockAll = () => {
		if (areAllLocked) {
			setLockedValues({});
		} else {
			const source =
				currentForcedFuture ||
				(sortedFutures.length > 0 ? sortedFutures[0] : null);
			if (!source) {
				return;
			}
			const next: Record<number, MinimalPlayerRatings> = {};
			for (const ageStr of Object.keys(source)) {
				const age = Number.parseInt(ageStr, 10);
				if (source[age]) {
					next[age] = source[age]!;
				}
			}
			setLockedValues(next);
		}
	};

	const handleYearRatingFormChange = async (
		type: string,
		field: string,
		event: React.ChangeEvent<HTMLInputElement>,
	) => {
		if (editingAge === null || !localRatings) return;

		if (type === "rating") {
			if (field === "locked") {
				setLocalRatings((prev) =>
					prev ? { ...prev, locked: event.target.checked } : null,
				);
			} else {
				const rawVal = event.target.value;
				const val = rawVal === "" ? 0 : Number.parseInt(rawVal, 10);
				if (!Number.isNaN(val)) {
					const updatedRatings = { ...localRatings, [field]: val };
					setLocalRatings(updatedRatings);

					const requestId = ++lastOvrRequestRef.current;
					// Recalculate overall rating (OVR)
					const newOvr = await toWorker("main", "ovr", {
						ratings: updatedRatings,
						pos,
						superchargeRatings,
					});

					if (requestId === lastOvrRequestRef.current) {
						setLocalRatings((prev) => (prev ? { ...prev, ovr: newOvr } : null));
					}
				}
			}
		}
	};

	const handleRandomizeYearRatings = async () => {
		if (editingAge === null || !localRatings) return;

		const { hgt, ratings } = await toWorker("main", "getRandomRatings", {
			age: editingAge,
			pos: bySport({
				baseball: pos,
				basketball: undefined,
				football: pos,
				hockey: pos,
			}),
		});

		const currentRatings = {
			...localRatings,
			...ratings,
			hgt,
		};

		const requestId = ++lastOvrRequestRef.current;
		// Recalculate overall rating (OVR)
		const newOvr = await toWorker("main", "ovr", {
			ratings: currentRatings,
			pos,
			superchargeRatings,
		});

		if (requestId === lastOvrRequestRef.current) {
			currentRatings.ovr = newOvr;
			setLocalRatings(currentRatings);
		}
	};

	const handleAdjustYearRatings = (amount: number) => async () => {
		if (editingAge === null || !localRatings) return;

		const currentRatings = { ...localRatings };

		for (const key of RATINGS) {
				if ((currentRatings as any)[key] !== undefined) {
					(currentRatings as any)[key] = helpers.bound(
						(currentRatings as any)[key] + amount,
					0,
					superchargeRatings ? 1000 : 100,
				);
			}
		}

		const requestId = ++lastOvrRequestRef.current;
		// Recalculate overall rating (OVR)
		const newOvr = await toWorker("main", "ovr", {
			ratings: currentRatings,
			pos,
			superchargeRatings,
		});

		if (requestId === lastOvrRequestRef.current) {
			currentRatings.ovr = newOvr;
			setLocalRatings(currentRatings);
		}
	};

	const handleSave = () => {
		if (editingAge === null || !localRatings) return;
		const source =
			currentForcedFuture ||
			(sortedFutures.length > 0 ? sortedFutures[0] : null);
		if (!source) return;

		const updatedFuture = { ...source };
		updatedFuture[editingAge] = localRatings;
		onFutureSelected(updatedFuture);
		setEditingAge(null);
	};

	const handleCancel = () => {
		setEditingAge(null);
	};

	return (
		<div className="mb-4 border rounded p-3 bg-light">
			{/* Pop-Up Ratings Editor Modal */}
			<Modal
				size="lg"
				show={editingAge !== null}
				onHide={handleCancel}
				scrollable
			>
				<Modal.Header closeButton>
					<Modal.Title>
						Edit Ratings for Age {editingAge}
						{showYear && editingAge !== null
							? ` (${season + editingAge - currentAge})`
							: ""}
					</Modal.Title>
				</Modal.Header>
				<Modal.Body>
					{editingAge !== null && (
						<div>
							<div className="d-flex justify-content-between align-items-center mb-3 border-bottom pb-2">
								<span className="text-muted">
									Modify individual ratings for this specific year of the forced
									progression.
								</span>
								<div className="d-flex align-items-center gap-2">
									<button
										type="button"
										className="btn btn-secondary btn-sm"
										onClick={handleRandomizeYearRatings}
										disabled={!godMode}
									>
										Randomize
									</button>
									<div className="btn-group">
										<button
											type="button"
											className="btn btn-secondary btn-sm"
											onClick={handleAdjustYearRatings(-1)}
											disabled={!godMode}
										>
											<span className="glyphicon glyphicon-minus" />
										</button>
										<button
											type="button"
											className="btn btn-secondary btn-sm"
											onClick={handleAdjustYearRatings(1)}
											disabled={!godMode}
										>
											<span className="glyphicon glyphicon-plus" />
										</button>
									</div>
								</div>
							</div>
							{localRatings && (
								<RatingsForm
									challengeNoRatings={challengeNoRatings}
									godMode={godMode}
									superchargeRatings={superchargeRatings}
									handleChange={handleYearRatingFormChange}
									pos={pos}
									ratingsRow={localRatings}
									lockedRatings={lockedRatings}
									onToggleLockedRating={onToggleLockedRating}
								/>
							)}
						</div>
					)}
				</Modal.Body>
				<Modal.Footer>
					<button className="btn btn-secondary" onClick={handleCancel}>
						Cancel
					</button>
					<button className="btn btn-primary" onClick={handleSave}>
						Save
					</button>
				</Modal.Footer>
			</Modal>
			<div className="d-flex justify-content-between align-items-center mb-2">
				<h3 className="mb-0">Monte Carlo Player Progressions</h3>
				<button
					type="button"
					className="btn btn-outline-primary btn-sm"
					onClick={() => setOpen(!open)}
				>
					{open ? "Collapse" : "Expand"}
				</button>
			</div>

			<Collapse in={open}>
				<div>
					<div className="d-flex justify-content-between mb-3">
						<div className="d-flex align-items-center">
							<Button
								variant="god-mode"
								className="me-2"
								onClick={runSimulations}
								disabled={loading}
							>
								{loading ? "Simulating..." : `Generate Futures`}
							</Button>
							<Form.Control
								type="number"
								className="me-2"
								value={numSimulations}
								onChange={(e) => {
									const val = Number.parseInt(e.target.value, 10);
									if (!Number.isNaN(val)) {
										setNumSimulations(Math.min(val, 500));
									}
								}}
								style={{ width: "80px" }}
							/>
							<Button
								variant="secondary"
								onClick={() => onFutureSelected(undefined)}
								disabled={!currentForcedFuture}
							>
								Clear Forced Future
							</Button>
						</div>
						<ButtonGroup>
							<Button
								variant={sortBy === "peak" ? "primary" : "secondary"}
								onClick={() => setSortBy("peak")}
							>
								Peak
							</Button>
							<Button
								variant={sortBy === "most-ovrs" ? "primary" : "secondary"}
								onClick={() => setSortBy("most-ovrs")}
							>
								Most OVRs
							</Button>
							<Button
								variant={sortBy === "consistency" ? "primary" : "secondary"}
								onClick={() => setSortBy("consistency")}
							>
								Consistency
							</Button>
							<Button
								variant={sortBy === "prodigy" ? "primary" : "secondary"}
								onClick={() => setSortBy("prodigy")}
							>
								Prodigy
							</Button>
							<Button
								variant={sortBy === "late-bloomer" ? "primary" : "secondary"}
								onClick={() => setSortBy("late-bloomer")}
							>
								Late Bloomer
							</Button>
							<Button
								variant={sortBy === "bust" ? "primary" : "secondary"}
								onClick={() => setSortBy("bust")}
							>
								Bust
							</Button>
						</ButtonGroup>
					</div>

					<div className="d-flex justify-content-between mb-3">
						<div className="d-flex align-items-center">
							<span className="me-2">Filter by Peak OVR:</span>
							<Form.Control
								type="number"
								className="me-2"
								placeholder="Min"
								value={minPeak}
								onChange={(e) => setMinPeak(e.target.value)}
								style={{ width: "80px" }}
							/>
							<Form.Control
								type="number"
								className="me-2"
								placeholder="Max"
								value={maxPeak}
								onChange={(e) => setMaxPeak(e.target.value)}
								style={{ width: "80px" }}
							/>
						</div>
						<div className="d-flex align-items-center">
							<span className="me-2">Luck:</span>
							<Form.Control
								type="number"
								min="-100"
								max="100"
								value={luck}
								onChange={(e) => {
									const val = Number.parseInt(e.target.value, 10);
									if (!Number.isNaN(val)) {
										setLuck(helpers.bound(val, -100, 100));
									}
								}}
								style={{ width: "80px" }}
							/>
							<Form.Check
								type="checkbox"
								id="show-year-checkbox"
								label="Show Year"
								className="ms-3"
								checked={showYear}
								onChange={(e) => setShowYear(e.target.checked)}
							/>
						</div>
					</div>

					<div className="d-flex justify-content-between mb-3">
						<div className="d-flex align-items-center">
							<span className="me-2">RPD Override:</span>
							<Form.Check
								type="checkbox"
								id="rpd-override-checkbox"
								className="me-2"
								checked={realPlayerDeterminism !== undefined}
								onChange={(e) => {
									if (e.target.checked) {
										setRealPlayerDeterminism(1);
									} else {
										setRealPlayerDeterminism(undefined);
									}
								}}
							/>
							{realPlayerDeterminism !== undefined && (
								<div className="d-flex align-items-center">
									<span className="me-2">
										{Math.round(realPlayerDeterminism * 100)}%
									</span>
									<input
										type="range"
										className="form-range"
										min="0"
										max="100"
										step="1"
										value={Math.round(realPlayerDeterminism * 100)}
										onChange={(e) => {
											const val = Number.parseInt(e.target.value, 10);
											if (!Number.isNaN(val)) {
												setRealPlayerDeterminism(val / 100);
											}
										}}
										style={{ width: "150px" }}
									/>
								</div>
							)}
							<HelpPopover title="Real Player Determinism Override">
								<p>
									Override the Real Player Determinism (RPD) setting for these
									simulations.
								</p>
								<p>
									RPD controls how much a player's development is based on their
									real-life career stats versus random variation.
								</p>
								<ul>
									<li>
										<b>0%:</b> Completely random development.
									</li>
									<li>
										<b>100%:</b> Development follows real-life career perfectly.
									</li>
								</ul>
								<p>
									<small>
										When disabled, uses the player's own RPD override or the
										league default.
									</small>
								</p>
							</HelpPopover>
						</div>
					</div>

					<div className="d-flex justify-content-between mb-3">
						<div className="d-flex align-items-start flex-wrap gap-3">
							<span className="me-2">Biased Ratings:</span>

							{/* Available Ratings Box */}
							<div>
								<div className="small text-muted mb-1">
									Available (double-click or drag)
								</div>
								<div
									className="border rounded p-2"
									style={{
										width: "180px",
										height: "150px",
										overflowY: "auto",
										background: "var(--bs-body-bg)",
									}}
									onDragOver={(e) => e.preventDefault()}
									onDrop={(e) => {
										e.preventDefault();
										const rating = e.dataTransfer.getData("text/plain");
										if (rating && rating in biasedRatings) {
											const newBiased = { ...biasedRatings };
											delete newBiased[rating];
											setBiasedRatings(newBiased);
										}
									}}
								>
									{RATINGS.filter((r) => !(r in biasedRatings)).map(
										(rating) => (
											<div
												key={rating}
												className="p-1 px-2 mb-1 rounded hover-bg-secondary cursor-pointer user-select-none"
												style={{ cursor: "pointer" }}
												draggable
												onDragStart={(e) => {
													e.dataTransfer.setData("text/plain", rating);
												}}
												onDoubleClick={() => {
													setBiasedRatings((prev) => ({
														...prev,
														[rating]: 1,
													}));
												}}
												onMouseEnter={(e) => {
													e.currentTarget.style.backgroundColor =
														"var(--bs-secondary-bg)";
												}}
												onMouseLeave={(e) => {
													e.currentTarget.style.backgroundColor = "transparent";
												}}
											>
												{getCol(`rating:${rating}`).desc}
											</div>
										),
									)}
								</div>
							</div>

							{/* Arrow indicator */}
							<div className="d-flex align-items-center">
								<span className="text-muted">→</span>
							</div>

							{/* Selected Ratings Box */}
							<div>
								<div className="small text-muted mb-1">
									Selected (double-click to remove)
								</div>
								<div
									className="border rounded p-2"
									style={{
										width: "280px",
										height: "150px",
										overflowY: "auto",
										background: "var(--bs-body-bg)",
									}}
									onDragOver={(e) => e.preventDefault()}
									onDrop={(e) => {
										e.preventDefault();
										const rating = e.dataTransfer.getData("text/plain");
										if (rating && !(rating in biasedRatings)) {
											setBiasedRatings((prev) => ({ ...prev, [rating]: 1 }));
										}
									}}
								>
									{Object.keys(biasedRatings).length === 0 ? (
										<div className="text-muted small fst-italic">
											Drag or double-click ratings here
										</div>
									) : (
										Object.entries(biasedRatings).map(([rating, value]) => (
											<div
												key={rating}
												className="d-flex align-items-center gap-2 p-1 px-2 mb-1 rounded"
												draggable
												onDragStart={(e) => {
													e.dataTransfer.setData("text/plain", rating);
												}}
												onDoubleClick={() => {
													const newBiased = { ...biasedRatings };
													delete newBiased[rating];
													setBiasedRatings(newBiased);
												}}
											>
												<span
													className="flex-grow-1"
													style={{ fontSize: "0.9rem" }}
												>
													{getCol(`rating:${rating}`).desc}
												</span>
												<Form.Control
													type="number"
													size="sm"
													min="0"
													max="100"
													step="0.5"
													value={value}
													onChange={(e) => {
														const val = Number.parseFloat(e.target.value);
														if (!Number.isNaN(val)) {
															setBiasedRatings((prev) => ({
																...prev,
																[rating]: helpers.bound(val, 0, 100),
															}));
														}
													}}
													style={{ width: "60px" }}
													onClick={(e) => e.stopPropagation()}
												/>
											</div>
										))
									)}
								</div>
							</div>

							<HelpPopover title="Ratings Bias">
								<p>Drag or double-click ratings from Available to Selected.</p>
								<p>
									Each selected rating has its own bias value that determines
									how much it grows each season.
								</p>
								<p>Double-click or drag back to remove.</p>
							</HelpPopover>
						</div>
					</div>

					<div className="mb-3 small">
						<strong>Legends:</strong>
						<span className="ms-2 badge bg-primary">OVR</span>
						<span className="ms-2 badge bg-success">Peak</span>
						<span className="ms-2">
							<u>Underlined</u> = Peak Age
						</span>
						<span className="ms-2">
							<b>Bold</b> = Peak OVR
						</span>
					</div>

					{sortedFutures.length > 0 ? (
						<div className="overflow-auto" style={{ maxHeight: "500px" }}>
							<Table
								striped
								bordered
								hover
								size="sm"
								className="text-center small mb-0"
								style={{ borderCollapse: "separate", borderSpacing: 0 }}
							>
								<thead
									style={{
										position: "sticky",
										top: 0,
										zIndex: 10,
										background: "var(--bs-body-bg)",
									}}
								>
									<tr>
										<th
											style={{
												position: "sticky",
												top: 0,
												background: "var(--bs-body-bg)",
												borderBottom: "1px solid var(--bs-border-color)",
											}}
										>
											<div
												className="form-check d-flex justify-content-center align-items-center"
												title="Lock/Unlock All"
											>
												<input
													className="form-check-input"
													type="checkbox"
													checked={areAllLocked}
													onChange={toggleLockAll}
													disabled={sortedFutures.length === 0}
												/>
												<span className="ms-1" style={{ fontSize: "0.8em" }}>
													Lock
												</span>
											</div>
										</th>
										<th
											style={{
												position: "sticky",
												top: 0,
												background: "var(--bs-body-bg)",
												borderBottom: "1px solid var(--bs-border-color)",
											}}
										></th>
										{Object.keys(sortedFutures[0]!).map((ageStr) => {
											const age = Number(ageStr);
											return (
												<th
													key={`lock-${age}`}
													style={{
														position: "sticky",
														top: 0,
														background: "var(--bs-body-bg)",
														borderBottom: "1px solid var(--bs-border-color)",
													}}
												>
													<div className="form-check d-flex justify-content-center">
														<input
															className="form-check-input"
															type="checkbox"
															checked={!!lockedValues[age]}
															onChange={() => toggleLock(age)}
															disabled={sortedFutures.length === 0}
														/>
													</div>
												</th>
											);
										})}
									</tr>
									<tr>
										<th
											style={{
												position: "sticky",
												top: "31px",
												background: "var(--bs-body-bg)",
												borderBottom: "1px solid var(--bs-border-color)",
											}}
										>
											Select
										</th>
										<th
											style={{
												position: "sticky",
												top: "31px",
												background: "var(--bs-body-bg)",
												borderBottom: "1px solid var(--bs-border-color)",
											}}
										>
											#
										</th>
										{Object.keys(sortedFutures[0]!).map((ageStr) => {
											const age = Number(ageStr);
											let sortIcon = null;
											if (
												typeof sortBy === "object" &&
												sortBy.type === "age" &&
												sortBy.age === age
											) {
												sortIcon = sortBy.direction === "asc" ? " ▲" : " ▼";
											}
											return (
												<th
													key={age}
													style={{
														position: "sticky",
														top: "31px",
														background: "var(--bs-body-bg)",
														borderBottom: "1px solid var(--bs-border-color)",
													}}
												>
													<span
														style={{ cursor: "pointer", userSelect: "none" }}
														onClick={() => {
															let direction: "asc" | "desc" = "desc";
															if (
																typeof sortBy === "object" &&
																sortBy.type === "age" &&
																sortBy.age === age &&
																sortBy.direction === "desc"
															) {
																direction = "asc";
															}
															setSortBy({ type: "age", age, direction });
														}}
													>
														{age}
														{showYear ? ` (${season + age - currentAge})` : ""}
														{sortIcon}
													</span>
												</th>
											);
										})}
									</tr>
								</thead>
								<tbody>
									{sortedFutures.map((future, i) => {
										const peakOvr = Math.max(
											...Object.values(future).map((r) => r.ovr),
										);
										const isSelected = isFutureSelected(future);

										return (
											<tr
												key={i}
												className={clsx(isSelected && "table-primary")}
											>
												<td
													style={
														isSelected
															? {
																	position: "sticky",
																	top: "62px",
																	zIndex: 5,
																	background: "var(--bs-table-active-bg)",
																}
															: {}
													}
												>
													<div className="form-check d-flex justify-content-center">
														<input
															className="form-check-input"
															type="checkbox"
															checked={isSelected}
															onChange={() => onFutureSelected(future)}
															disabled={!godMode}
														/>
													</div>
												</td>
												<td
													style={
														isSelected
															? {
																	position: "sticky",
																	top: "62px",
																	zIndex: 5,
																	background: "var(--bs-table-active-bg)",
																}
															: {}
													}
												>
													{isSelected ? "CURRENT" : i + 1}
												</td>
												{Object.keys(future).map((ageKey) => {
													const age = Number(ageKey);
													const r = future[age];
													if (!r) {
														return (
															<td
																key={age}
																style={
																	isSelected
																		? {
																				position: "sticky",
																				top: "62px",
																				zIndex: 5,
																				background: "var(--bs-table-active-bg)",
																			}
																		: {}
																}
															></td>
														);
													}
													const isPeak = r.ovr === peakOvr;
													return (
														<td
															key={age}
															style={
																isSelected
																	? {
																			position: "sticky",
																			top: "62px",
																			zIndex: 5,
																			background: "var(--bs-table-active-bg)",
																		}
																	: {}
															}
															className={clsx(
																godMode && "rating-cell-clickable",
															)}
															onClick={() => {
																if (godMode) {
																	setEditingAge(age);
																}
															}}
															title={formatRatingsTooltip(r)}
														>
															<span
																className={clsx(
																	isPeak &&
																		"text-success fw-bold text-decoration-underline",
																)}
															>
																{r.ovr}
															</span>
														</td>
													);
												})}
											</tr>
										);
									})}
								</tbody>
							</Table>
						</div>
					) : (
						<p className="text-muted">
							No futures generated yet. Click the button to simulate potential
							outcomes.
						</p>
					)}
				</div>
			</Collapse>
		</div>
	);
};

export default memo(PlayerFutures);

// CSS for instant tooltip (no delay)
const instantTooltipStyles = `
.rating-cell-clickable {
	cursor: pointer;
	transition: background-color 0.15s ease-in-out, color 0.15s ease-in-out;
}

.rating-cell-clickable:hover {
	background-color: var(--bs-primary-bg-subtle) !important;
	color: var(--bs-primary-text-emphasis) !important;
}

.instant-tooltip-container {
	cursor: help;
}

.instant-tooltip {
	position: absolute;
	top: 100%;
	left: 50%;
	transform: translateX(-50%);
	background: var(--bs-dark);
	color: var(--bs-light);
	padding: 10px 14px;
	border-radius: 6px;
	font-size: 0.8rem;
	z-index: 10000;
	opacity: 0;
	visibility: hidden;
	pointer-events: none;
	box-shadow: 0 4px 12px rgba(0, 0, 0, 0.4);
	margin-top: 6px;
	min-width: 180px;
	text-align: left;
	line-height: 1.4;
}

.instant-tooltip-container:hover .instant-tooltip {
	opacity: 1;
	visibility: visible;
	transition: opacity 0.05s ease-out;
}

.instant-tooltip::after {
	content: '';
	position: absolute;
	bottom: 100%;
	left: 50%;
	transform: translateX(-50%);
	border: 5px solid transparent;
	border-bottom-color: var(--bs-dark);
}

.instant-tooltip div {
	margin: 1px 0;
}

.instant-tooltip div strong {
	color: var(--bs-info);
}
`;

// Inject styles
if (typeof document !== "undefined") {
	const styleId = "instant-tooltip-styles";
	if (!document.getElementById(styleId)) {
		const styleEl = document.createElement("style");
		styleEl.id = styleId;
		styleEl.textContent = instantTooltipStyles;
		document.head.appendChild(styleEl);
	}
}
